import { useCallback, useEffect } from 'react'

import { useErrorBoundary } from 'react-error-boundary'

import { Entries } from '@/constants/content-stack'
import { FetchStatus } from '@/constants/general'
import { CMSUnrecoverableError } from '@/errors/classes/CMSUnrecoverableError'
import { setEntryState } from '@/services/content-stack/actions/set-entry-state'
import { ContentStackStore } from '@/services/content-stack/content-stack.store'
import { FetchErrorOption } from '@/services/content-stack/types'

export function useErrorObserver(
  entry?: Entries[keyof Entries],
  onError?: FetchErrorOption<unknown>['onError'],
) {
  const entryState = ContentStackStore(
    ({ entries }) => entry && entries.get(entry),
  )
  const { showBoundary } = useErrorBoundary()

  const onDefaultError = useCallback(
    (errorPayload: unknown) =>
      showBoundary(new CMSUnrecoverableError(errorPayload)),
    [showBoundary],
  )

  const setFallbackEntry = useCallback(
    (entryFallback: unknown) => {
      setEntryState(entry!, FetchStatus.SUCCESS, entryFallback)
    },
    [entry],
  )

  useEffect(() => {
    if (entryState?.status === FetchStatus.ERROR) {
      const { errorPayload } = entryState
      OobeLogger.error('CMS Error', errorPayload, String(errorPayload))
      onError
        ? onError(errorPayload, setFallbackEntry)
        : onDefaultError(errorPayload)
    }
  }, [entryState, onError, onDefaultError, setFallbackEntry])
}
